import React from 'react';
import PropTypes from 'prop-types';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { Modal } from 'react-bootstrap-5';
import Select from 'react-select';

import Bool from './bool';
import Enum from './enum';
import IconSelect from './icon_select';
import { getErrors, getSelectValue, parseSelectValue, trimValues } from './option';
import { formInputStyles, getOptions } from 'helpers/react_select_helpers';
import OptionalLiquidInput, { valueTypes } from './optional_liquid_input';

import './styles/form_modal.scss';

const defaultOption = {
  default: {
    type: valueTypes.exact,
    value: false
  },
  hidden: {
    type: valueTypes.exact,
    value: false
  },
  id: '',
  icon: null,
  label: '',
  multiselect: false,
  type: 'bool',
  values: []
};

export const multiselectType = 'multiselect';

class OptionFormModal extends React.Component {
  static propTypes = {
    index: PropTypes.number,
    liquidContext: PropTypes.object.isRequired,
    option: PropTypes.object,
    options: PropTypes.array.isRequired,
    show: PropTypes.bool,

    onCancel: PropTypes.func.isRequired,
    onOk: PropTypes.func.isRequired
  }

  state = {
    option: { ...defaultOption },
    errors: [],
    liquidValidity: {}
  }

  getDefaultValue(type, multiselect) {
    switch (type) {
    case Bool.type:
      return false;
    case Enum.type:
      return multiselect ? [] : this.state.option.values[0] || '';
    }
  }

  handleEnter = () => {
    this.setState({
      errors: [],
      option: { ...defaultOption, ...this.props.option }
    });
  }

  handleChange = (event) => {
    const { checked, name, value } = event.target;
    this.setState(({ option }) => {
      const changes = {};

      if (name === 'default' || name === 'hidden') {
        changes[name] = {
          ...option[name],
          value: checked
        };
      } else {
        changes[name] = value;
      }

      return {
        option: { ...option, ...changes }
      };
    });
  }

  handleTypeChange = (event) => {
    let type = event.target.value;

    this.setState(({ option }) => {
      const changes = {};

      if (type === Enum.type) {
        changes.multiselect = false;
      } else if (type === multiselectType) {
        type = Enum.type;
        changes.multiselect = true;
      }

      changes.type = type;

      if (option.default.type === valueTypes.exact) {
        changes.default = {
          ...option.default,
          value: this.getDefaultValue(type, changes.multiselect)
        };
      }

      return {
        option: { ...option, ...changes }
      };
    });
  }

  handleEnumDefaultChange = (selectOption) => {
    this.setState(({ option }) => ({
      option: {
        ...option,
        default: {
          ...option.default,
          value: parseSelectValue(option, selectOption)
        }
      }
    }));
  }

  handleLiquidChange = (value, name) => {
    this.setState(({ option }) => ({
      option: {
        ...option,
        [name]: { ...option[name], value }
      }
    }));
  }

  handleValueTypeChange = (type, name) => {
    this.setState(({ liquidValidity, option }) => {
      let value;
      if (type === valueTypes.liquid) {
        value = '{{  }}';
      } else if (name === 'default') {
        value = this.getDefaultValue(option.type, option.multiselect);
      } else {
        value = false;
      }

      return {
        liquidValidity: {
          ...liquidValidity,
          [name]: type !== valueTypes.liquid
        },
        option: {
          ...option,
          [name]: { ...option[name], type, value }
        }
      };
    });
  }

  handleLiquidValidityChange = (valid, name) => {
    this.setState(({ liquidValidity }) => ({
      liquidValidity: {
        ...liquidValidity,
        [name]: valid
      }
    }));
  }

  handleIconChange = (icon) => {
    this.setState(({ option }) => ({
      option: { ...option, icon }
    }));
  }

  addValue = () => {
    this.setState(({ option }) => ({
      option: {
        ...option,
        values: option.values.concat('')
      }
    }));
  }

  handleEnumValueChange = (event) => {
    const { dataset, value } = event.target;
    const index = parseInt(dataset.index);

    this.setState(({ option }) => {
      const oldValue = option.values[index];
      const values = option.values.concat();
      values[index] = value;

      const changes = { values };

      if (option.multiselect) {
        const defaultValues = { ...option.default.value };
        if (defaultValues.hasOwnProperty(oldValue)) {
          delete defaultValues[oldValue];
          changes.default = {
            ...option.default,
            value: { ...defaultValues, [value]: true }
          };
        }
      } else if (option.default.value === oldValue && option.default.type === valueTypes.exact) {
        changes.default = { ...option.default, value };
      }

      return {
        option: { ...option, ...changes }
      };
    });
  }

  deleteEnumValue = (event) => {
    const index = parseInt(event.target.closest('button').dataset.index);

    this.setState(({ option }) => {
      const values = option.values;
      const value = option.values[index];
      const changes = {
        values: values.slice(0, index).concat(values.slice(index + 1))
      };

      if (option.multiselect) {
        const defaultValues = { ...option.default.value };
        delete defaultValues[value];

        changes.default = {
          ...option.default,
          value: defaultValues
        };
      } else if (values[index] === option.default.value) {
        changes.default = {
          ...option.default,
          value: changes.values[0]
        };
      }

      return {
        option: { ...option, ...changes }
      };
    });
  }

  handleOk = () => {
    const option = trimValues(this.state.option);
    const errors = getErrors(option, this.props.options, this.props.index);

    for (const name in this.state.liquidValidity) {
      if (!this.state.liquidValidity[name]) {
        errors.push(`Liquid for ${name} must be valid`);
      }
    }

    this.setState({ errors });

    if (errors.length === 0) {
      if (option.type === Bool.type) {
        // Only clear this here so type changes don't wipe out values early
        delete option.values;
      }

      // These will be immediately fixed by the updated preview, just set them so they are present
      option.default.evaluated = option.type === Enum.type && option.multiselect ? [] : '';
      option.hidden.evaluated = '';

      this.props.onOk(option);
    }
  }

  render() {
    return (
      <Modal
        className="option-form-modal"
        show={this.props.show}
        onEnter={this.handleEnter}
        onHide={this.props.onCancel}
      >
        <Modal.Header closeButton>
          <Modal.Title>{this.props.option ? 'Edit' : 'Add'} Option</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {this.renderInputs()}
        </Modal.Body>

        <Modal.Footer>
          {this.renderErrors()}

          <button className="btn btn-ghost" name="cancel" onClick={this.props.onCancel}>
            Cancel
          </button>

          <button className="btn btn-primary" name="ok" onClick={this.handleOk}>
            OK
          </button>
        </Modal.Footer>
      </Modal>
    );
  }

  renderInputs() {
    let renderExactInput;
    switch (this.state.option.type) {
    case Bool.type:
      renderExactInput = this.renderBoolDefaultInput;
      break;
    case Enum.type:
      renderExactInput = this.renderEnumDefaultInput;
      break;
    }

    return <React.Fragment>
      <label className="form-label">Label *</label>
      <input
        className="form-control"
        name="label"
        value={this.state.option.label}
        onChange={this.handleChange}
      />

      <label className="form-label">ID *</label>
      <input
        className="form-control"
        name="id"
        value={this.state.option.id}
        onChange={this.handleChange}
      />

      <label className="form-label">Icon *</label>
      <IconSelect
        value={this.state.option.icon}
        onChange={this.handleIconChange}
      />

      <label className="form-label">Type *</label>
      <select
        className="form-control"
        name="type"
        value={this.state.option.multiselect ? multiselectType : this.state.option.type}
        onChange={this.handleTypeChange}
      >
        <option value={Bool.type}>Boolean</option>
        <option value={Enum.type}>Select</option>
        <option value="multiselect">Multiselect</option>
      </select>

      <label className="form-label" htmlFor="hidden">Hidden</label>
      <OptionalLiquidInput
        liquidContext={this.props.liquidContext}
        name="hidden"
        renderExactInput={this.renderHiddenInput}
        value={this.state.option.hidden.value}
        valueType={this.state.option.hidden.type}
        onValidityChange={this.handleLiquidValidityChange}
        onValueChange={this.handleLiquidChange}
        onValueTypeChange={this.handleValueTypeChange}
      />

      <label className="form-label" htmlFor="default">Default</label>
      <OptionalLiquidInput
        liquidContext={this.props.liquidContext}
        name="default"
        renderExactInput={renderExactInput}
        value={this.state.option.default.value}
        valueType={this.state.option.default.type}
        onValidityChange={this.handleLiquidValidityChange}
        onValueChange={this.handleLiquidChange}
        onValueTypeChange={this.handleValueTypeChange}
      />

      {this.renderTypeInputs()}
    </React.Fragment>;
  }

  renderHiddenInput = () => {
    return (
      <div className="form-check">
        <input
          className="form-check-input"
          id="hidden"
          name="hidden"
          type="checkbox"
          checked={this.state.option.hidden.value}
          onChange={this.handleChange}
        />
      </div>
    );
  }

  renderBoolDefaultInput = () => {
    return (
      <div className="form-check">
        <input
          className="form-check-input"
          id="default"
          name="default"
          type="checkbox"
          checked={this.state.option.default.value}
          onChange={this.handleChange}
        />
      </div>
    );
  }

  renderEnumDefaultInput = () => {
    return (
      <Select
        className="default-input"
        classNamePrefix=""
        isClearable={false}
        isMulti={this.state.option.multiselect}
        name="default"
        options={getOptions(this.state.option.values)}
        styles={formInputStyles}
        value={getSelectValue(this.state.option, this.state.option.default.value)}
        onChange={this.handleEnumDefaultChange}
      />
    );
  }

  renderTypeInputs() {
    switch (this.state.option.type) {
    case Enum.type:
      return this.renderEnumInputs();
    default:
      return null;
    }
  }

  renderEnumInputs() {
    return <React.Fragment>
      <label className="form-label">Values *</label>
      {this.renderEnumValues()}

      <div>
        <button className="btn btn-primary" name="add-value" onClick={this.addValue}>
          Add Value
        </button>
      </div>
    </React.Fragment>;
  }

  renderEnumValues() {
    return <React.Fragment>
      {(this.state.option.values).map((value, i) => (
        <div className="option-value" key={i}>
          <input
            className="form-control right-buffer-md"
            data-index={i}
            value={value}
            onChange={this.handleEnumValueChange}
          />

          <div className="text-danger">
            <button
              className="btn btn-delete"
              data-index={i}
              name="delete-value"
              onClick={this.deleteEnumValue}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>
      ))}
    </React.Fragment>;
  }

  renderErrors() {
    return (
      <div className="errors">
        {this.state.errors.map(error => (
          <div key={error} className="text-danger" name="error">{error}</div>
        ))}
      </div>
    );
  }
}

export default OptionFormModal;
